import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { RoleModal } from "@/components/roles/RoleModal";
import { logCrud } from "@/services/activities";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Role {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  scope: string;
  permissions_count?: number;
}

const Roles = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: roles, isLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const { data: rolesData, error: rolesError } = await supabase
        .from("roles" as any)
        .select("*")
        .order("name", { ascending: true }) as any;
      
      if (rolesError) throw rolesError;

      // Buscar count de permissions para cada role
      const rolesWithCount = await Promise.all(
        (rolesData || []).map(async (role: any) => {
          const { count, error: countError } = await supabase
            .from("role_permissions_junction" as any)
            .select("*", { count: 'exact', head: true })
            .eq("role_id", role.id);

          if (countError) {
            console.error("Erro ao contar permissões:", countError);
            return { ...role, permissions_count: 0 };
          }

          return { ...role, permissions_count: count || 0 };
        })
      );
      
      return rolesWithCount as Role[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("roles" as any)
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["roles"] });
      toast.success("Perfil excluído com sucesso!");
      if (roleToDelete) {
        void logCrud({ entitySlug: "entity:roles", operationSlug: "delete", entityId: roleToDelete, screenSlug: "perfis" });
      }
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    },
    onError: (error: any) => {
      toast.error("Erro ao excluir perfil: " + error.message);
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    },
  });

  const handleEdit = (role: Role) => {
    setSelectedRole(role);
    setIsModalOpen(true);
  };

  const handleDelete = (id: string) => {
    setRoleToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (roleToDelete) {
      deleteMutation.mutate(roleToDelete);
    }
  };

  const handleNewRole = () => {
    setSelectedRole(null);
    setIsModalOpen(true);
  };

  // Pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRoles = roles?.slice(startIndex, endIndex) || [];
  const totalPages = Math.ceil((roles?.length || 0) / itemsPerPage);

  return (
    <div className="space-y-6" data-cy="roles-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-cy="roles-title">Perfis de Acesso</h1>
          <p className="text-muted-foreground">
            Gerencie os perfis e suas permissões
          </p>
        </div>
        <Button onClick={handleNewRole} data-cy="new-role-btn">
          <Plus className="mr-2 h-4 w-4" />
          Novo Perfil
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle data-cy="roles-list-title">Lista de Perfis</CardTitle>
          <CardDescription>
            {roles?.length || 0} {roles?.length === 1 ? "perfil" : "perfis"} cadastrados
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8" data-cy="roles-loading">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <div className="border rounded-md">
                <Table data-cy="roles-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Slug</TableHead>
                      <TableHead>Escopo</TableHead>
                      <TableHead>Permissões</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-cy="roles-table-body">
                    {paginatedRoles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground" data-cy="roles-empty-state">
                          Nenhum perfil encontrado
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedRoles.map((role) => (
                        <TableRow key={role.id}>
                          <TableCell className="font-medium">{role.name}</TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {role.slug}
                            </code>
                          </TableCell>
                          <TableCell>
                            <Badge variant={role.scope === "global" ? "default" : "secondary"}>
                              {role.scope === "global" ? "Global" : "Tenant"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {role.permissions_count || 0} {role.permissions_count === 1 ? "permissão" : "permissões"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="icon-chip-green"
                                onClick={() => handleEdit(role)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="icon-chip-red"
                                onClick={() => handleDelete(role.id)}
                                disabled={role.slug === "admin_saas" || role.slug === "admin"}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <TablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={roles?.length || 0}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(value) => {
                    setItemsPerPage(value);
                    setCurrentPage(1);
                  }}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      <RoleModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRole(null);
        }}
        role={selectedRole}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-cy="role-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este perfil? Esta ação não pode ser desfeita.
              Todos os usuários associados a este perfil perderão suas permissões.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-cy="role-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-cy="role-delete-confirm">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Roles;
